import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ExternalLink,
} from 'lucide-react';

interface ImageLightboxProps {
  isOpen: boolean;
  src: string;
  alt: string;
  onClose: () => void;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  src,
  alt,
  onClose,
}) => {
  const [zoom, setZoom] = useState(1);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.5, 4));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.5, 0.5));
  const handleResetZoom = () => setZoom(1);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col justify-between p-4 animate-in fade-in duration-200 select-none"
      onClick={onClose}
    >
      {/* Top Header Bar */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-slate-950/80 rounded-2xl border border-slate-800 text-white z-10 max-w-4xl mx-auto w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-xs font-semibold truncate max-w-md text-slate-200">
          {alt}
        </span>

        {/* Toolbar controls */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleZoomIn}
            className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition"
            title="Zoom in (+)"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleZoomOut}
            className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition"
            title="Zoom out (-)"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleResetZoom}
            className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition"
            title="Reset Zoom"
          >
            <Maximize2 className="h-4 w-4" />
          </button>

          <div className="h-4 w-px bg-slate-800 mx-1" />

          <a
            href={src}
            download={alt}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition inline-flex items-center gap-1 text-xs font-semibold"
            title="Download image"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Download</span>
          </a>

          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl hover:bg-slate-800 text-slate-300 hover:text-white transition"
            title="Open original in new tab"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-red-500/20 text-slate-300 hover:text-red-400 transition ml-2"
            title="Close (ESC)"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Image Container */}
      <div
        className="flex-1 flex items-center justify-center overflow-auto p-4 cursor-zoom-out"
        onClick={onClose}
      >
        <img
          src={src}
          alt={alt}
          style={{ transform: `scale(${zoom})` }}
          className="max-h-[82vh] max-w-full rounded-xl object-contain shadow-2xl transition-transform duration-200 border border-slate-800/80 bg-slate-950/40 select-none cursor-default"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Footer Info */}
      <div
        className="text-center text-xs text-slate-400 py-1"
        onClick={(e) => e.stopPropagation()}
      >
        Press <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-200 font-mono text-[10px]">ESC</kbd> or click outside to close
      </div>
    </div>
  );
};
